import type { GetSettingsListOneResponseI } from "@Common/types/bo/settings/settings";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { useAuthState, useDataProvider } from "react-admin";
import { useLocation } from "react-router-dom";

type QuotaWarningContextValue = {
	showFormsWarning: boolean;
	showApplicationsWarning: boolean;
	quotaData: GetSettingsListOneResponseI | null;
	isLoading: boolean;
};

const QuotaWarningContext = createContext<QuotaWarningContextValue | undefined>(
	undefined,
);

// NOTE: we must use here a check that we are authenticated, we cannot rely on PublicModeProvider
// because public components update that provider state when they mount so it's asynchronous
// So when page is initially loaded, isPublic = false even for public components, which would cause AppBar
// to be renderer for a very short time, QuotaWarningProvider to fetch settings and hit a 401, and cause login
// redirection
export const QuotaWarningProvider = ({ children }: { children: ReactNode }) => {
	const [quotaData, setQuotaData] =
		useState<GetSettingsListOneResponseI | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const dataProvider = useDataProvider();
	const location = useLocation();
	// the second arg false means it won't trigger a logout on auth failure
	const { authenticated, isPending } = useAuthState(undefined, false);

	// Fetch quota data on mount and on every route change, only if authenticated
	// biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally want to refetch on location.pathname change
	useEffect(() => {
		if (isPending || !authenticated) return;

		const fetchQuotaData = async () => {
			try {
				setIsLoading(true);
				const response = await dataProvider.getList("settings", {
					pagination: { page: 1, perPage: 1 },
					sort: { field: "id", order: "ASC" },
					filter: {},
				});

				if (response.data && response.data.length > 0) {
					setQuotaData(response.data[0] as GetSettingsListOneResponseI);
				}
			} catch (error) {
				console.error("Failed to fetch quota data:", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchQuotaData();
	}, [dataProvider, location.pathname, authenticated, isPending]); // Re-fetch on every page navigation

	// Calculate warnings based on thresholds
	const showFormsWarning =
		!isLoading &&
		quotaData !== null &&
		quotaData.remaining_forms !== null &&
		quotaData.remaining_forms <= 2;

	const showApplicationsWarning =
		!isLoading &&
		quotaData !== null &&
		quotaData.max_applications_per_month > 0 &&
		quotaData.remaining_applications <=
			quotaData.max_applications_per_month * 0.1; // 10% or less remaining

	return (
		<QuotaWarningContext.Provider
			value={{
				showFormsWarning,
				showApplicationsWarning,
				quotaData,
				isLoading,
			}}
		>
			{children}
		</QuotaWarningContext.Provider>
	);
};

export const useQuotaWarning = () => {
	const ctx = useContext(QuotaWarningContext);
	if (!ctx)
		throw new Error("useQuotaWarning must be used inside QuotaWarningProvider");
	return ctx;
};
